import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button, OutlineButton, SubtleButton } from "components/button";
import Field from "components/field";
import MultiInput from "components/input/multi";
import { useModalContext } from "components/modal";
import { useSnackbarContext } from "components/snackbar";
import CustomDocModal from "containers/customDocModal";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { TProperty } from "types/property";
import CustomerTag from "views/customer/customer-tag";
import { useAreYouSure } from "components/areYouSure";
import { FaInfoCircle } from "react-icons/fa";
import TooltippedElement from "components/TooltippedElement";
import { useTranslation } from 'react-i18next';
import FileGrid from "components/file-grid";
import { generatePdfThumbnail } from "components/pdf-thumbnail";
import { MdAttachFile } from "react-icons/md";
import { Link } from "react-router-dom";
import { FiExternalLink } from "react-icons/fi";

type Props = {
  invoiceId: string;
  type: "request" | "project";
  typeId: string;
  emails: string[] | null;  
  isResend?: boolean;
  /** business_customer.fk_billing_address_id — used when invoice has no saved billing_address snapshot */
  fkBillingAddressId?: number | string | null;
};

export default function SendInvoiceModal(props: Props) {
  const { t } = useTranslation();
  const tBase = "views.invoice.sendinvoiceModal";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const snackbar = useSnackbarContext();
  const queryClient = useQueryClient();
  const modal = useModalContext();
  const { showSnackbar } = useSnackbarContext();
  const areYouSure = useAreYouSure();

  const {
    invoiceId,
    type,
    typeId,
    isResend = false,
    fkBillingAddressId,
  } = props;

  const [templateId, setTemplateId] = useState<string>();
  const [emails, setEmails] = useState<(string | null)[] | null>();
  const [billing, setBilling] = useState<TProperty>();
  const [mailOnly, setMailOnly] = useState<boolean>(false);
  const [completion_notes, setCompletionNotes] = useState("");
  const [includeHeaders, setIncludeHeaders] = useState(true);
  const [noColor, setNoColor] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [onlyInvoiceFiles, setOnlyInvoiceFiles] = useState<boolean>(false);
  const [invoiceFiles, setInvoiceFiles] = useState<string[]>([]);
  const hasFetchedInvoiceFiles = useRef(false);
  const [taxesValid, setTaxesValid] = useState<boolean>(true);
  const [taxesValidityMessage, setTaxesValidityMessage] = useState<string>("");

  const inputRef = useRef<HTMLInputElement>(null);

const { data: sendDetailsPayload, isLoading: isSendDetailsLoading } =
  useQuery(
    ["invoice-send-details", invoiceId],  
    async () =>
      await axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/${invoiceId}/send-details`)
        .then((res) => res.data),
    {
      staleTime: 0,              
      cacheTime: 0,              
      refetchOnWindowFocus: false,
      onSuccess: (details) => {
        if (details?.sendDetails?.[0]) {
          const persisted = details.sendDetails[0].billing_address;
          if (persisted && typeof persisted === "object") {
            setBilling(persisted as TProperty);
          }
          const docId = details.sendDetails[0].fk_customdoc_id;
          setTemplateId(docId != null && docId !== "" ? String(docId) : undefined);
          setCompletionNotes(details.sendDetails[0].completion_notes);
          setInvoiceFiles(details.sendDetails[0].files || []);
          if (details.mailOnly) {
            setMailOnly(details.mailOnly);
          }
        }
      },
    }
  );

const billingAddress = useQuery<TProperty[] | null>(
  ["billing address", invoiceId],   // ← add invoiceId
  async () =>
    await axios
      .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/${invoiceId}/billing_address`)
      .then((res) => res.data),
  {
    staleTime: 0,    
    cacheTime: 0,    
  }
);

  useEffect(() => {
    const list = billingAddress.data;
    if (!list?.length) return;

    const invBill = sendDetailsPayload?.sendDetails?.[0]
      ?.billing_address as TProperty | null | undefined;

    const hasPersisted =
      invBill != null &&
      typeof invBill === "object" &&
      "id" in invBill &&
      invBill.id != null;

    if (hasPersisted) {
      const matched = list.find(
        (a) => String(a.id) === String((invBill as TProperty).id)
      );
      setBilling(matched ?? (invBill as TProperty));
      return;
    }

    if (
      fkBillingAddressId === undefined ||
      fkBillingAddressId === null ||
      fkBillingAddressId === ""
    ) {
      return;
    }

    const matched = list.find(
      (a) => String(a.id) === String(fkBillingAddressId)
    );
    if (matched) {
      setBilling(matched);
    }
  }, [billingAddress.data, sendDetailsPayload, fkBillingAddressId]);

  const mutation = useMutation(
    async () => {
      const formData = new FormData();

      attachments.forEach((file) => {
        if (file.new) { // new file
          if (file.editedName) {
            const newFile = new File([file.fullFile], file.editedName, { type: file.fullFile.type });
            const newThumbnail = new File([file.thumbnailFile], file.editedName, { type: file.thumbnailFile.type });
        
            formData.append("attachments", newFile);
            formData.append("thumbnails", newThumbnail);
          } else {
            formData.append("attachments", file.fullFile); 
            formData.append("thumbnails", file.thumbnailFile); 
          }
        } else { // fetched from request/project
          formData.append("fetchedFiles", JSON.stringify({ 
            name: file.name,
            editedName: file.editedName || "",
            key: file.name, 
            url: file.url,
            type,
            typeId
          }));
        }
      });
      
      formData.append("templateId", templateId != null ? String(templateId) : "");
      formData.append("emails", JSON.stringify(emails));
      formData.append("completion_notes", completion_notes);
      formData.append("noColor", noColor.toString());
      formData.append("includeHeaders", includeHeaders.toString());
      formData.append("type", type);
      formData.append("typeId", typeId);
      formData.append("billing", JSON.stringify(billing));
      formData.append("isResend", isResend.toString());
      // add fromWeb flag to indicate coming from web, not field
      formData.append("fromWeb", "true");

      return await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/${invoiceId}/send`,
        formData
      );
    },
    {
      onSuccess: () => {
        showSnackbar(tr("Invoice sent!"));
        queryClient.invalidateQueries();
        modal.setModal();
      },
      onError: () => {
        showSnackbar(tr("Error sending invoice"));
      },
    }
  );

  const preview = useMutation(
    async () => await axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/${invoiceId}/preview`,
          {
            params: {
              templateId,
              completion_notes,
              billing: billing ? JSON.stringify(billing) : undefined,
              includeHeaders,
              noColor,
            },
          }
        )
        .then((res) => res.data),
    {
      onSuccess: (url) => {
        window.open(url, "_blank");
      },
    }
  );

  const { refetch: refetchFiles } = useQuery(["requestOrProjectFiles", type, typeId, onlyInvoiceFiles ? invoiceId : null],
    async () => {
      return await axios
        .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/files/${type}/${typeId}`,
          {
            params: {
              invoiceId: onlyInvoiceFiles ? invoiceId : undefined,
            }
          }
        )
        .then((res) => res.data);
    },
    {
      refetchOnWindowFocus: false,
      enabled: false
    },
  );

  const handlePreview = () => {
    preview.mutate();
  };

  const attachmentUpload = () => {
    inputRef.current?.click();
  };

  // handles new files
  const handleFileSelection = (files: FileList) => {
    try {
      setAttachments((prev) => {
        const validExtensions = [".pdf", ".png", ".jpg", ".jpeg"];
        const uniqueFiles = Array.from(files).filter((file) => {
          const fileExtension = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
          const isValidExtension = validExtensions.includes(fileExtension);
          const isUnique = !prev.some((existingFile) => existingFile.name === file.name);

          if (!isValidExtension) {
            showSnackbar(tr("Invalid file type, supported types are") + ": PDF, PNG, JPG, JPEG");
          } else if (!isUnique) {
            showSnackbar(tr("File already added"));
          }

          return isValidExtension && isUnique;
        });

        const processedFiles = uniqueFiles.map(async (file) => {
          if (file.type.startsWith("image/")) {
            return {
              name: file.name,
              type: file.type,
              url: URL.createObjectURL(file), // For images, use a preview URL
              thumbnail: URL.createObjectURL(file),
              thumbnailFile: file,
              new: true,
              fullFile: file,
            };
          } else if (file.type === "application/pdf") {
            const thumbnail = await generatePdfThumbnail(file);
            const thumbnailBlob = thumbnail && await fetch(thumbnail).then((res) => res.blob());
            const thumbnailFile = thumbnailBlob && new File([thumbnailBlob], file.name, { type: "image/png" });
            return {
              name: file.name,
              type: file.type,
              url: URL.createObjectURL(file), // Thumbnail for PDF
              thumbnail: thumbnail, // Preview URL for PDF
              thumbnailFile: thumbnailFile,
              new: true,
              fullFile: file,
              isPdf: true,
            };
          } else {
            return {
              name: file.name,
              type: file.type,
              url: null, // Unsupported file types
              thumbnail: null,
              new: true,
              fullFile: file,
            };
          }
        });
    
        // Wait for all thumbnail generation to complete
        Promise.all(processedFiles).then((finalFiles) => {
          setAttachments([...prev, ...finalFiles]);
        });
    
        return prev;
      });
    } catch (e) {
      showSnackbar(tr("Error adding file(s)"));
    }
  };

  // gets all files sent from previous sending of invoice
  useEffect(() => {
    const shouldFetch =
      isResend &&
      !isSendDetailsLoading &&
      invoiceFiles.length > 0 &&
      !hasFetchedInvoiceFiles.current;

    if (shouldFetch) {
      hasFetchedInvoiceFiles.current = true; // Prevent repeated fetches
      addFiles(true);
    }
  }, [isResend, isSendDetailsLoading, invoiceFiles]);


  const addFiles = async (fromInvoice: boolean = false) => {
    try {
      setOnlyInvoiceFiles(fromInvoice);
      if (fromInvoice && invoiceFiles.length === 0) return;
      // Small delay to ensure state updates before refetch
      await new Promise(resolve => setTimeout(resolve, 0));

      const { data: fetchedFiles } = await refetchFiles(); 
      if (fetchedFiles) {
        setAttachments((prev) => {
          const uniqueFiles = fetchedFiles
            .filter((file: any) => file.signedUrl) 
            .map((file: any) => ({
              name: file.Key, 
              url: file.signedUrl, 
              type: file.type, 
              size: file.Size, 
              lastModified: file.LastModified, 
            }))
            .filter((file: any) => 
              !prev.some((existingFile) => existingFile.name === file.name) // Prevent duplicates
            );
  
          return [...prev, ...uniqueFiles];
        });
      }
    } catch (error) {
      showSnackbar(tr("Error adding file(s)"));
      console.error("Error fetching files:", error);
    } finally {
      setOnlyInvoiceFiles(false);
    }
  };

  useEffect(() => {
    if (props.emails) {
      setEmails(props.emails);
    }
  }, [props.emails]);

  
  const checkTaxesValidity = async () => {
    const result = await axios.get(`
      ${process.env.REACT_APP_SERVER_URL}/api/v1/company/tax/checkTaxesValidity/${invoiceId}
    `);
    return result.data;
  };

  useEffect(() => {
  axios
    .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/tax/checkTaxesValidity/${invoiceId}`, {
      headers: { 'Cache-Control': 'no-cache' }, 
    })
    .then((res) => {
      setTaxesValid(Boolean(res.data?.valid));
      setTaxesValidityMessage(res.data?.message || "");
    })
    .catch(() => {
      setTaxesValid(false);
      setTaxesValidityMessage("");
    });
}, [invoiceId]);

  const visibleAddresses = showAll
    ? billingAddress.data
    : billingAddress.data?.slice(0, 5);

  const populateBillingAddresses = visibleAddresses?.map((address) => {
    return (
      <label key={address.id} className="flex items-center space-x-2 space-y-2">
        <input
          type="radio"
          value={address.id}
          name="billing_address"
          onChange={(e: any) => {
            setBilling(address);
          }}
          checked={billing?.id === address.id}
          className="mt-2.5"
        />
        <div className="text-wrap">
          {address.full_address}
          {String(address.id) === String(fkBillingAddressId) && (
            <span className="ml-2 text-xs font-semibold text-green-800 bg-green-100 px-2 py-0.5 rounded">{tr("Default billing")}</span>
          )}
          {address.name && (
            <span className="text-gray-500 italic"> ({address.name})</span>
          )}
        </div>
      </label>
    );
  });

  const totalSizeInMB = attachments.reduce((sum, file) => {
    const size = file.size ?? file.fullFile?.size ?? 0;
    return sum + size;
  }, 0) / (1024 * 1024);

  const totalSizeStr = `${totalSizeInMB.toFixed(2)} MB`;

  const maxSizeInMB = 10;
  const isOverLimit = totalSizeInMB > maxSizeInMB;

  return (
    <div className="max-w-[500px] space-y-4 mb-4">
      {mailOnly && <CustomerTag color="red" label={tr("Mail Only")} />}
      <MultiInput values={emails} onChange={(e) => setEmails(e)} />
      <CustomDocModal
        id={templateId}
        type="invoice"
        onClick={(id) => setTemplateId((tId) => (tId === id ? undefined : id))}
      />
      <div className="my-2">
        <h3>{tr("Billing Address")}</h3>
        <div className="flex flex-col">{populateBillingAddresses}</div>
      </div>
      <div className="flex justify-center">
        {billingAddress && (billingAddress.data ?? []).length > 5 && (
          <button
            type="button"
            onClick={() => setShowAll(!showAll)}
            className="text-blue-600 hover:underline text-sm"
          >
            {showAll ? tr("Show Less") : tr("Show More")}
          </button>
        )}
      </div>
      <div>
        <h3>{tr("Completion Notes")}</h3>
        <div className="flex flex-col">
          <Field
            type="textarea"
            value={completion_notes}
            onChange={(e) => setCompletionNotes(e.target.value)}
            minRows={5}
          />
        </div>
      </div>
      <div className="flex items-center [&>*]:-my-2.5">
        <input 
          type="checkbox" 
          checked={includeHeaders} 
          onChange={() => setIncludeHeaders(!includeHeaders)} 
        />
        <label className="ml-2">{tr("Include Headers")}</label>
        <TooltippedElement 
          message={tr("Turn On/Off Headers for Mail Only Customers")}
          placement="top"
          element={
            <FaInfoCircle className="ml-1 text-secondary" />
          }
        />
      </div>
      <div className="flex items-center [&>*]:-mt-1.5 [&>*]:-mb-2.5">
        <input 
          type="checkbox" 
          checked={noColor} 
          onChange={() => setNoColor(!noColor)} 
        />
        <label className="ml-2">{tr("Black & White Print")}</label>
        <TooltippedElement 
          message={tr("Turn Off Color for Printing PDF")}
          placement="top"
          element={
            <FaInfoCircle className="ml-1 text-secondary" />
          }
        />
      </div>
      {/* Attachments */}
      <div className="mt-2">
        <div className="flex items-center justify-between">
          <div className="text-[12.8px] ">{tr("Attachments")}:</div>
          {/*  Update the size display section: */}
          {attachments.length > 0 && (
            <div className="text-xs text-gray-700">
              <span className={`font-medium ${isOverLimit ? 'text-red-500' : 'text-gray-500'}`}>
                {totalSizeStr}
              </span> 
              <span className={isOverLimit ? 'text-red-500' : 'text-gray-500'}>
                {" "} / {maxSizeInMB} MB
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between">
          {attachments.length > 0 && (
            <div>
              <button
                className="text-xs inline-block text-red-400 underline hover:no-underline"
                onClick={() => setAttachments([])}
                type="button"
              >
                {tr("Remove All Attachments")}
              </button>
            </div>
          )}
          {isOverLimit && (
            <div className="text-red-500 text-xs">
              {tr("You are over the maximum attachment size limit")}
            </div>
          )}
        </div>
        <div className="mt-2">
          {attachments.length > 0 ? (
            <FileGrid
              id={typeId}
              type={type}
              files={attachments}
              setFiles={setAttachments}
              refetch={refetchFiles}
              isLocal={true}
            />
          ) : (
            <div className="text-gray-500">{tr("No attachments")}</div>
          )}
        </div>
        </div>
      <div className=" h-[10px]" />

      {/* Limit error message */}
      {isOverLimit && (
        <div className="text-red-500 text-xs w-full text-right !-mt-1 !-mb-1">
          {tr("You are over the maximum attachment size limit")}
        </div>
      )}

      {/* Taxes validation message */}
      {!taxesValid && (
        <div className="text-red-500 text-xs w-full text-right !-mt-1 !-mb-1 flex flex-col items-end">
          <div>{tr("One or more taxes have not been added or synced with QuickBooks Online.")}</div>
          {taxesValidityMessage && (
            <div className="max-w-md text-right">{taxesValidityMessage}</div>
          )}
          <div className="flex items-center space-x-[1px]">
            <div>{tr("Please add them before sending the invoice.")}</div>
            <Link 
              to="/admin/tax" 
              target="_blank"
              rel="noopener noreferrer"
              className="text-secondary no-underline hover:underline flex items-center"
            >
              <div>{tr("Taxes")}</div><FiExternalLink className="ml-[2px] text-secondary" />
            </Link>
          </div>
        </div>
      )}
      
      {/* Buttons */}
      <div className="flex justify-end space-x-2">
        {isResend && (
          <TooltippedElement
            message={`${tr("Add previously attached files from this invoice")}`}
            placement="top"
            element={
              <Button onClick={() => addFiles(true)} className="relative">
                <MdAttachFile />
                <div className="absolute top-0 right-2 text-[16px]">+</div>
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-[8px]">
                  {tr("Invoice")}
                </div>
              </Button>
            }
          />
        )}
        <TooltippedElement
          message={`${type === "request" ? tr("Add files from request") : tr("Add files from project")}`}
          placement="top"
          element={
            <Button onClick={() => addFiles()} className="relative">
              <MdAttachFile />
              <div className="absolute top-0 right-2 text-[16px]">+</div>
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-[8px]">
                {type === "request" ? tr("Request") : tr("Project")}
              </div>
            </Button>
          }
        />
        <Button onClick={attachmentUpload}>
          <MdAttachFile />
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.png,.jpg,.jpeg"
          onClick={(e) => {
            (e.target as HTMLInputElement).value = ""; // Reset file input
          }}
          onChange={(e) => {
            if (e.target.files) {
              handleFileSelection(e.target.files);
            }
          }}
          className="hidden"
        />
        <SubtleButton
          isLoading={preview.isLoading}
          disabled={!templateId || !billing || preview.isLoading}
          onClick={handlePreview}
        >
          {tr("Preview")}
        </SubtleButton>
        <Button
          primary
          type="submit"
          isLoading={mutation.isLoading}
          disabled={emails?.length === 0 || !templateId || !billing || isOverLimit || mutation.isLoading || !taxesValid}
          onClick={() => {
            if (mailOnly) {
              // Prompt the user before sending the invoice
              // areYouSure.activate(() => mutation.mutate(), {
              //   message:
              //     "This customer has a 'Mail Only' tag. Are you sure you want to proceed with sending the invoice?",
              // });
              areYouSure.activate(() => mutation.mutate(), {
                message:
                  tr("This customer has a 'Mail Only' tag. Are you sure you want to proceed with sending the invoice?"),
              });
            } else {
              // Directly send the invoice if there's no 'Mail Only' condition
              mutation.mutate();
            }
          }}
        >
          {props.isResend ? tr("Resend Invoice") : tr("Send Invoice")}
        </Button>
        <OutlineButton onClick={() => modal.setModal()}>{tr("Cancel")}</OutlineButton>
      </div>
    </div>
  );
}
